import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { passengerCMSApi, post } from "~/Services";
import { passengerHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import {
  passengerAirportColumn,
  renderActionsColumn,
  renderDateColumn,
  renderPublishedColumnStatus,
} from "~/Utils/TableRenderFunction";
import { useSubCategoriesDocumentDetails } from "./usefulltips.hooks";

export const UsefullTips = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(undefined);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedPublished, setSelectedPublished] = useState<any>("");
  const [currentAirport] = useLocalStorage("currentAirport", null);
  // ✅ FIX: use booleans for published dropdown values
  const publishedData: TypeData[] = [
    { label: "Yes", value: "True" },
    { label: "No", value: "False" },
  ];

  const [data, setData] = useState<any[]>([]);
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");

  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const { data: drilldown, refetch: refetchDocument } = useSubCategoriesDocumentDetails(id || "");

  useEffect(() => {
    if (id) refetchDocument();
  }, [id]);

  // reset filters when tab changes
  useEffect(() => {
    if (query.get("activeTab") === "subCategories") {
      setSelectedPublished("");
      setSearchValue("");
      setCurrentPage(0);
    }
  }, [query.get("activeTab")]);

  // Fetch records
  const getRecords = async () => {
    setIsLoading(true);
    try {
      const url = `/api/useful-tips-all-fields-ecmsss?airport=${currentAirport}`;
      const res = await passengerCMSApi.get(url);

      const raw = res?.data;

      setData(raw);
      setSortedData(raw);
      setTotalRecords(raw.length);
    } catch (err) {
      setData([]);
      setSortedData([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRecords();
  }, []);

  // Filtering: search + published
  useEffect(() => {
    let filtered = [...data];

    if (searchValue) {
      const searchLower = decodeHTML(searchValue).toLowerCase();
      filtered = filtered.filter((item) =>
        decodeHTML(item.title || "")
          .toLowerCase()
          .includes(searchLower)
      );
    }

    if (selectedPublished !== "") {
      const isPublished = String(selectedPublished).toLowerCase() === "true";
      filtered = filtered.filter((item: any) => {
        const itemPublished = String(item.published).toLowerCase() === "true";
        return itemPublished === isPublished;
      });
    }

    setSortedData(filtered);
    setTotalRecords(filtered.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  const columns: TableColumnsType<any> = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      width: 150,
      render: (src: string) =>
        src ? (
          <img
            src={src}
            alt="thumb"
            style={{
              width: 120,
              height: 72,
              objectFit: "cover",
              borderRadius: 6,
            }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 250,
      render: (text: string) => decodeHTML(text || ""),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "title"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "title"
            ? "descend"
            : undefined,
    },
    {
      title: "Airports",
      dataIndex: "airports",
      key: "airports",
      width: 400,
      render: (text) => passengerAirportColumn(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "airports"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "airports"
            ? "descend"
            : undefined,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      width: 120,
      render: renderPublishedColumnStatus,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created",
      key: "created",
      render: (value: any) => renderDateColumn(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "changed",
      key: "changed",
      render: (value: any, record: any) => {
        if (value && record.created && value === record.created) {
          return "-";
        }
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "changed"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "changed"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_: any, record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/passenger/homepage/usefulltips/add?editId=${record.id}`);
          },
          undefined,
          () => {
            setSelectedRowData(record);
            setDeleteModalVisible(true);
          },
          "users"
        ),
    },
  ];

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  // Table sort handler: toggles asc/desc and sorts sortedData
  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (sortOrder === "asc" && orderByName === column.dataIndex) {
      setSortOrder("desc");
      order = "desc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);
    const tempData = [...sortedData];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    dataPayload: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (!apiUrl) return;

    const payload = {
      action,
      method,
      module: "Home Page",
      subModule: "usefulTips_usefulTipsPageList",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      airportName: currentAirport,
      reviewNotes,
      appList: [{ appName: "PASSENGER_ECMS", apiUrl }],
      payload: dataPayload,
      status,
    };

    try {
      const response: any = await post("/management/publish", payload);
      openToast({ content: "Data Sent for Review", type: "success" });
      if (response.status === "OK" && status === "PENDING") {
        navigate("/dedicated/status-information?activeTab=pending");
      }
    } catch {
      openToast({ content: "Failed to send data", type: "error" });
    }
  };

  async function sendDeleteRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${passengerHost}/api/useful-tips-crud-api-data/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {
      console.error("❌ Delete failed:", err);
    }
  }

  const handlePublishedChange = (value: any) => {
    // value will now be boolean true/false (or "" if cleared)
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={currentPageData || []}
        isLoading={isLoading}
        exportFileName="USEFULL_TIPS"
        isPagination={true}
        recordsPerPage={10}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText="Search Title"
        onClick={(record) => setSelectedRowData(record)}
        additionalCTA={
          <ActionDropdown
            value={selectedPublished}
            options={publishedData}
            onChange={handlePublishedChange}
            placeholder="Select Published"
            className="w-48"
          />
        }
      />

      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "usefulTipsPageList") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteRequest}
        defaultReviewNotes="Kindly review this Homepage"
      />
    </>
  );
};

/**
 * handleSort: robust comparator that handles strings, numbers, booleans.
 * - For string-like fields: case-insensitive compare.
 * - For 'published' boolean: true > false in ascending order.
 */
export function handleSort(a: any, b: any, order: string | undefined, sortBy: string) {
  const valA = a[sortBy];
  const valB = b[sortBy];

  // boolean handling (explicit)
  if (typeof valA === "boolean" || typeof valB === "boolean") {
    const numA = valA ? 1 : 0;
    const numB = valB ? 1 : 0;
    if (order === "desc") return numA < numB ? 1 : -1;
    if (order === "asc") return numA > numB ? 1 : -1;
    return 0;
  }

  // string handling
  const sA = valA !== undefined && valA !== null ? String(valA).toLowerCase() : "";
  const sB = valB !== undefined && valB !== null ? String(valB).toLowerCase() : "";

  if (order === "desc") {
    return sA < sB ? 1 : -1;
  }
  if (order === "asc") {
    return sA > sB ? 1 : -1;
  }
  return 0;
}

export default UsefullTips;
